import { Export } from "@phosphor-icons/react";
import clsx from "clsx";
import { type JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RestrictedComponent from "common/components/RestrictedComponent";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAudience from "../../../../datahooks/useAudience";
import useCancelledAudience from "../../../../datahooks/useCancelledAudience";
import useCampaignDetailsAudience from "../../../../hooks/useCampaignDetailsAudience";
import { exportProfilesToCSV } from "../../../../utils";
import ManageMobileAudience from "../../ManageAudience/ManageMobileAudience";
import SectionHeading from "../../SectionHeading";
import AddTarget from "./AddTarget";
import AudienceDesktopFilters from "./DesktopLayout/AudienceDesktopFilters";
import AudienceDesktopTable from "./DesktopLayout/AudienceDesktopTable";
import AudienceMobileList from "./MobileLayout/AudienceMobileList";
import MobileFilters from "./MobileLayout/MobileFilters";

import type { AudienceRow } from "../../../../types";

import EmptyPlaceholderImage from "assets/images/empty-placeholders/no-profiles.png";

export default function CampaignAudience({
  openLeadPreview,
  isPreviewOpen: _isPreviewOpen,
}: {
  openLeadPreview: (leadId: string) => void;
  isPreviewOpen: boolean;
}) {
  const { t } = useTranslation("campaigns");
  const isDesktop = useTwBreakpoint("lg");
  const [selectedTargets, setSelectedTargets] = useState<AudienceRow[]>([]);

  const { audience, isLoadingAudience, isAudienceError, refetchAudience } =
    useAudience();
  const { cancelledAudience } = useCancelledAudience();

  const {
    filteredTargets,
    resetFilters,
    selectedStates,
    setSelectedStates,
    searchText,
    setSearchText,
    audienceWithCancellation,
  } = useCampaignDetailsAudience(audience, cancelledAudience);

  if (isAudienceError)
    return (
      <ErrorPlaceholder
        errorMessage="Error loading audience"
        onRetry={refetchAudience}
      />
    );

  const desktopFiltersComponent = (
    <AudienceDesktopFilters
      selectedStates={selectedStates}
      setSelectedStates={setSelectedStates}
      audienceWithCancellation={audienceWithCancellation}
    />
  );

  const filterEmptyStateComponent = (
    <EmptyPlaceholder
      imageSrc={EmptyPlaceholderImage}
      title="No results found"
      subtitle="There were no results that match your criteria"
      actionComponent={<Button onClick={resetFilters}>Clear</Button>}
    />
  );

  let mainComponent: JSX.Element;

  if (isLoadingAudience || audience.length) {
    const tableProps = {
      rows: isLoadingAudience ? [] : filteredTargets,
      isLoading: isLoadingAudience,
      selectedTargets,
      setSelectedTargets,
    };

    if (isDesktop) {
      mainComponent = (
        <AudienceDesktopTable
          {...tableProps}
          filtersComponent={desktopFiltersComponent}
          emptyStateComponent={filterEmptyStateComponent}
          openLeadPreview={openLeadPreview}
        />
      );
    } else if (filteredTargets.length || isLoadingAudience) {
      mainComponent = <AudienceMobileList {...tableProps} />;
    } else {
      mainComponent = filterEmptyStateComponent;
    }
  } else {
    mainComponent = (
      <EmptyPlaceholder
        imageSrc={EmptyPlaceholderImage}
        title="No targets found"
        subtitle="This campaign has no targets yet."
      />
    );
  }

  const profilesToExport = selectedTargets.length
    ? selectedTargets.map(({ urn }) =>
        audience.find(({ urn: targetUrn }) => targetUrn === urn),
      )
    : filteredTargets;

  const addTargetComponent = (
    <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
      <AddTarget />
    </RestrictedComponent>
  );

  return (
    <div className="flex flex-col gap-4">
      <SectionHeading
        title={t("campaignDetails.audienceTitle")}
        subtitle={t("campaignDetails.audienceSubtitle")}
      />

      <div className="flex w-full grow flex-col gap-y-4">
        <div className="flex flex-col w-full gap-3">
          <div className="flex gap-2.5 flex-col lg:flex-row">
            <div className="flex gap-x-2.5 flex-1">
              <SearchInput
                value={searchText}
                placeholder="Search audience"
                variant={isDesktop ? "sm" : "lg"}
                onChange={(e) => setSearchText(e.currentTarget.value)}
                className="max-lg:flex-1 lg:max-w-72"
                onClear={() => setSearchText("")}
              />
              {!isDesktop && (
                <MobileFilters
                  selectedStates={selectedStates}
                  setSelectedStates={setSelectedStates}
                  audienceWithCancellation={audienceWithCancellation}
                />
              )}
            </div>

            <div className="lg:ml-auto flex flex-row gap-2.5 lg:gap-2">
              {addTargetComponent}

              {!isDesktop && selectedTargets.length ? (
                <ManageMobileAudience
                  selectedTargets={selectedTargets}
                  resetSelectedTargets={() => setSelectedTargets([])}
                />
              ) : (
                <Button
                  leftIcon={<Export weight="fill" />}
                  variant="secondary-purple"
                  size={isDesktop ? "sm" : "md"}
                  className={clsx(
                    "flex-1",
                    !filteredTargets.length &&
                      "pointer-events-none bg-black-200 text-black-400",
                  )}
                  asChild
                >
                  <a
                    href={exportProfilesToCSV(profilesToExport)}
                    download="LinkedInProfiles.csv"
                  >
                    Export audience
                    <span className="max-lg:hidden">
                      {" "}
                      ({filteredTargets.length})
                    </span>
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="h-px w-full bg-black-200 hidden lg:block" />

        {mainComponent}
      </div>
    </div>
  );
}
